enum Day {
  Sunday = 'Sunday',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday'
}

enum Month {
  January = 'January',
  February = 'February',
  March = 'March',
  April = 'April',
  May = 'May',
  June = 'June',
  July = 'July',
  August = 'August',
  September = 'September',
  October = 'October',
  November = 'November',
  December = 'December'
}

enum TimeSuffix {
  AM = 'am',
  PM = 'pm'
}

enum DateSuffix {
  ST = 'st',
  ND = 'nd',
  RD = 'rd',
  TH = 'th'
}

export default class DateService {
  private static readonly NOON_HOUR = 12
  private static readonly TIME_SEPARATOR = ':'
  private static readonly DATE_SEPARATOR = '-'

  static getMonthFullNames (): Month[] {
    return [
      Month.January,
      Month.February,
      Month.March,
      Month.April,
      Month.May,
      Month.June,
      Month.July,
      Month.August,
      Month.September,
      Month.October,
      Month.November,
      Month.December
    ]
  }

  static getDayFullNames (): Day[] {
    return [
      Day.Sunday,
      Day.Monday,
      Day.Tuesday,
      Day.Wednesday,
      Day.Thursday,
      Day.Friday,
      Day.Saturday
    ]
  }

  static getSuffixForDate (date: Date): string {
    switch (date.getDate()) {
      case 1:
      case 21:
      case 31:
        return DateSuffix.ST
      case 2:
      case 22:
        return DateSuffix.ND
      case 3:
      case 23:
        return DateSuffix.RD
      default:
        return DateSuffix.TH
    }
  }

  static monthAsString (index: number): Month {
    return DateService.getMonthFullNames()[index]
  }

  static monthAsShortString (index: number): string {
    return DateService.monthAsString(index).substr(0, 3)
  }

  static dayAsString (index: number): Day {
    return DateService.getDayFullNames()[index]
  }

  static dayAsShortString (index: number): string {
    return DateService.dayAsString(index).substr(0, 3)
  }

  static getToday (): Date {
    const now = new Date()

    now.setHours(0)
    now.setMinutes(0)
    now.setSeconds(0)
    now.setMilliseconds(0)

    return now
  }

  static getYesterday (): Date {
    const today = DateService.getToday()

    today.setDate(today.getDate() - 1)

    return today
  }

  static getTomorrow (): Date {
    const today = DateService.getToday()

    today.setDate(today.getDate() + 1)

    return today
  }

  static padNumber (digits: number, value: number): string {
    let str = ''

    for (let i = 0; i < digits; i++) {
      str += '0'
    }

    return (str + value).substr(-digits, digits)
  }

  static formatDateForAPI (date: Date): string {
    return [
      date.getFullYear(),
      DateService.padNumber(2, (date.getMonth() + 1)),
      DateService.padNumber(2, date.getDate())
    ].join(DateService.DATE_SEPARATOR)
  }

  static formatDateForCache (date: Date): number {
    const regex = new RegExp(`[${DateService.DATE_SEPARATOR}]`, 'g')
    let str = DateService.formatDateForAPI(date)

    str = str.replace(regex, '')

    return parseInt(str)
  }

  static formatTimeForAPI (time: Date): string {
    return [
      DateService.padNumber(2, time.getHours()),
      DateService.padNumber(2, time.getMinutes())
    ].join(DateService.TIME_SEPARATOR)
  }

  static formatDateFromAPI (date: string): Date {
    return new Date(date + 'T00:00:00')
  }

  static getNextMonth (date: Date): Date {
    const thisMonth = DateService.getStartOfMonth(new Date())

    thisMonth.setMonth(
      thisMonth.getMonth() + 1
    )

    return thisMonth
  }

  static getStartOfMonth (date: Date): Date {
    const startOfMonth = DateService.cloneDate(date)

    startOfMonth.setDate(1)

    return DateService.getStartOfDay(startOfMonth)
  }

  static getStartOfDay (date: Date): Date {
    const startOfDay = DateService.cloneDate(date)

    startOfDay.setHours(0)
    startOfDay.setMinutes(0)
    startOfDay.setSeconds(0)

    return startOfDay
  }

  static isSame (date1: Date, date2: Date): boolean {
    return date1.getTime() === date2.getTime()
  }

  static isSameDate (date1: Date, date2: Date): boolean {
    const date1Day = DateService.getStartOfDay(date1)
    const date2Day = DateService.getStartOfDay(date2)

    return DateService.isSame(date1Day, date2Day)
  }

  static isSameMonth (date1: Date, date2: Date): boolean {
    const date1Month = DateService.getStartOfMonth(date1)
    const date2Month = DateService.getStartOfMonth(date2)

    return DateService.isSame(date1Month, date2Month)
  }

  static cloneDate (date: Date): Date {
    return new Date(date.valueOf())
  }

  static addMinutesToTime (minutes: number, time: any): Date {
    const date = new Date()

    date.setHours(time.hours)
    date.setMinutes(time.minutes + minutes)
    date.setSeconds(0)
    date.setMilliseconds(0)

    return date
  }

  static addHoursToTime (hours: number, time: any): Date {
    const date = new Date(time.valueOf())

    date.setTime(date.getTime() + (hours*60*60*1000));
    date.setSeconds(0)
    date.setMilliseconds(0)

    return date
  }

  static addDaysToTime (days: number, time: any): Date {
    const date = new Date(time.valueOf())

    date.setDate(date.getDate() + days);
    date.setSeconds(0)
    date.setMilliseconds(0)

    return date
  }

  static friendlyTime (time: any, appendSuffix: boolean = true): string {
    let hour = time.hours
    let min = time.minutes

    let suffix = TimeSuffix.AM

    if (hour >= DateService.NOON_HOUR) {
      suffix = TimeSuffix.PM

      if (hour > DateService.NOON_HOUR) hour -= DateService.NOON_HOUR
    }

    return hour + DateService.TIME_SEPARATOR + DateService.padNumber(2, min) + (appendSuffix ? suffix : '')
  }

  static friendlyDate (fullDate: Date): string {
    let day = DateService.dayAsShortString(fullDate.getDay())
    let date = fullDate.getDate()
    let month = DateService.monthAsShortString(fullDate.getMonth())
    let year = fullDate.getFullYear()

    return day + ' ' + date + DateService.getSuffixForDate(fullDate) + ' ' + month + ' ' + year
  }
}



// WEBPACK FOOTER //
// ./src/services/DateService.ts